import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";

import { MasterPrivacyPolicyUrl, UserTermsOfService } from "common/tos";
import { Paragraph } from "common/core/typography";
import { useQuery } from "util/graphql";
import BundleNotFoundModal from "common/document_bundle/bundle_not_found_modal";
import { onlyRequiresEsign } from "util/completion_requirements/completion_requirements_text";
import LoadingIndicator from "common/core/loading_indicator";

import SignerLandingQuery, {
  type SignerLanding_documentBundle_DocumentBundle as DocumentBundle,
  type SignerLanding_viewer as Viewer,
} from "./index.query.graphql";
import { BitLandingV2 } from "./content/generic";
import { RealEstateLanding } from "./content/real_estate";
import { EsignLanding } from "./content/esign";
import { AuthenticatedEsignLanding } from "./content/authenticated_esign";

type Props = {
  documentBundle: DocumentBundle;
  viewer: Viewer;
  readOnly?: boolean;
};

type TermsMessageProps = {
  isNotEsign: boolean;
};

export const TermsMessage = ({ isNotEsign }: TermsMessageProps) => {
  const EsignCTA = (
    <FormattedMessage id="054f830e-dc9c-45e3-8316-7cd008f635a6" defaultMessage="'View Documents'" />
  );
  const CTA = (
    <FormattedMessage
      id="e2ae6635-fddb-4422-8d2e-4c3703c69d32"
      defaultMessage="'Get it notarized'"
    />
  );

  return (
    <Paragraph size="small" textColor="subtle" textAlign="center">
      <FormattedMessage
        id="c3965ba8-0997-4a3c-92c4-cfaee4a0801a"
        defaultMessage="By clicking {ctaText}, or signing up with your Google account, you are agreeing to the {termsOfUse}. For information on our privacy and data use practices please see our {privacyPolicy}."
        values={{
          termsOfUse: <UserTermsOfService underlined />,
          privacyPolicy: <MasterPrivacyPolicyUrl underlined />,
          ctaText: isNotEsign ? CTA : EsignCTA,
        }}
      />
    </Paragraph>
  );
};

function LandingComponent({ documentBundle, viewer, readOnly }: Props) {
  const { completionRequirements } = documentBundle;

  const isAuthenticatedEsign = documentBundle.participants!.find(
    (p) => p!.userId === viewer.user!.id,
  )!.proofRequirement?.ca?.selfie;
  const isEsign = onlyRequiresEsign(completionRequirements) && !isAuthenticatedEsign;
  let Component;

  if (documentBundle.isMortgage) {
    // Render real_estate_content.tsx for all real estate transactions(including standard RON created by lender/title)
    Component = RealEstateLanding;
  } else if (isAuthenticatedEsign) {
    // Render authenticated_esign.tsx for transactions requiring esign authentication with Persona
    Component = AuthenticatedEsignLanding;
  } else if (isEsign) {
    // Render esign_content.tsx for all esign transactions that have customizable landing pages enabled
    Component = EsignLanding;
  } else {
    // Render v2_content.tsx for all other organizations/transaction types
    Component = BitLandingV2;
  }

  return <Component documentBundle={documentBundle} viewer={viewer} readOnly={readOnly} />;
}

function BitLanding({ documentBundle, viewer, readOnly }: Props) {
  return <LandingComponent documentBundle={documentBundle} viewer={viewer} readOnly={readOnly} />;
}

function BitLandingWrapper({
  previewDocumentBundle,
  previewViewer,
}: {
  previewDocumentBundle?: DocumentBundle;
  previewViewer?: Viewer;
}) {
  const [searchParams] = useSearchParams();
  const documentBundleId = searchParams.get("id") || "";

  const { data, loading } = useQuery(SignerLandingQuery, {
    variables: {
      documentBundleId,
    },
    skip: !documentBundleId,
  });

  if (previewDocumentBundle && previewViewer) {
    return <BitLanding readOnly documentBundle={previewDocumentBundle} viewer={previewViewer} />;
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  const { documentBundle, viewer } = data!;

  if (!documentBundle) {
    // documentBundle can be null if bundle was recalled
    return <BundleNotFoundModal />;
  }

  if (documentBundle.__typename !== "DocumentBundle") {
    throw new Error(`Expected DocumentBundle, got ${documentBundle.__typename}.`);
  }

  return <BitLanding documentBundle={documentBundle} viewer={viewer} />;
}

export default BitLandingWrapper;
